// The base game and the New Wonders and Leaders expansion: which cards a game's decks have (Factories/DeckLists.js), which
// leaders and wonders, and which version of the base game's cards the expansion changed (Hammurabi, Colossus, Republic,
// ...). A changed leader's or wonder's definition (Leaders.js, Wonders.js) is the base game's, with its expansion changes
// under `expansion`; a changed technology's class takes the version. Each such card in a game says which version it is
// (card.version, from its card data: 'expansion' or 'base'; none: the base game's), and a technology in play is in the
// game's setup's version.
const Versions = { Base: 'base', Expansion: 'expansion' };
// The leaders and wonders in the decks: the base game's (all of them); the expansion's (all of them: its pure expansion
// game); every one of both; or a mix, picked at random from both when the game is set up (setup.selection, see
// PickLeadersAndWonders)
const CardSets = { Base: 'base', Expansion: 'expansion', All: 'all', Mix: 'mix' };
// For a mix, what the players know of the leaders and wonders picked: nothing until each is drawn (secret); all of them
// from the start (public); each age's as it begins (byAge). See Server/Views.js.
const Reveals = { Secret: 'secret', Public: 'public', ByAge: 'byAge' };
// For a mix, what its leaders and wonders are picked from (setup.pick; none: All): every one of both sets; the
// expansion's (all of them) with one of the base game's leaders and one of its wonders for each age, as the rulebook's
// "Other Variants" has it; or both sets less the ones left out (setup.excluded: names)
const Picks = { All: 'all', New: 'new', Custom: 'custom' };
// The other cards in the decks: the base game's, or with the expansion's additions (new cards, more copies of some)
const DeckSets = { Base: 'base', Expansion: 'expansion' };

// A game's setup: the decks, the leaders and wonders it has, the version of the changed cards (overrides: a version by
// card name)
const Expansion = { deck: DeckSets.Expansion, versions: Versions.Expansion, overrides: {} };
const Setups = {
    Base: { deck: DeckSets.Base, cards: CardSets.Base, versions: Versions.Base, overrides: {} },
    // The expansion with every leader and wonder of both (not a way the rules play it: for the card gallery and tests)
    Expansion: { ...Expansion, cards: CardSets.All },
    PureExpansion: { ...Expansion, cards: CardSets.Expansion },
    SecretMix: { ...Expansion, cards: CardSets.Mix, reveal: Reveals.Secret },
    PublicMix: { ...Expansion, cards: CardSets.Mix, reveal: Reveals.Public },
    RevealedMix: { ...Expansion, cards: CardSets.Mix, reveal: Reveals.ByAge },
};
// (a new game's: the rules' recommended way to play the expansion)
const DefaultSetup = Setups.PublicMix;
// The setups by the names the API takes
const SetupNames = { base: Setups.Base, expansion: Setups.Expansion, pure: Setups.PureExpansion, secret: Setups.SecretMix,
    public: Setups.PublicMix, byAge: Setups.RevealedMix };

// The base game's leaders and wonders (the rest come with the expansion)
const LeaderNames = require('./Names/LeaderNames');
const WonderNames = require('./Names/WonderNames');
const BaseGameCards = new Set([
    LeaderNames.AlexanderTheGreat, LeaderNames.Aristotle, LeaderNames.Hammurabi, LeaderNames.Homer, LeaderNames.JuliusCaesar,
    LeaderNames.Moses, LeaderNames.ChristopherColumbus, LeaderNames.FrederickBarbarossa, LeaderNames.GenghisKhan,
    LeaderNames.JoanOfArc, LeaderNames.LeonardoDaVinci, LeaderNames.Michelangelo, LeaderNames.IsaacNewton,
    LeaderNames.JamesCook, LeaderNames.JohannesSebastianBach, LeaderNames.MaximillienRobespierre,
    LeaderNames.NapoleonBonaparte, LeaderNames.WilliamShakespeare, LeaderNames.AlbertEinstein, LeaderNames.BillGates,
    LeaderNames.CharlesChaplin, LeaderNames.MahatmaGandhi, LeaderNames.SidMeier, LeaderNames.WinstonChurchill,
    WonderNames.Colossus, WonderNames.HangingGardens, WonderNames.LibraryOfAlexandria, WonderNames.Pyramids,
    WonderNames.GreatWall, WonderNames.StPetersBasilica, WonderNames.TajMahal, WonderNames.UniversitasCarolina,
    WonderNames.EiffelTower, WonderNames.Kremlin, WonderNames.OceanLiner, WonderNames.TranscontinentalRailroad,
    WonderNames.FastFoodChains, WonderNames.FirstSpaceFlight, WonderNames.Hollywood, WonderNames.Internet,
]);

// Whether a leader or wonder is in a game with this setup (of an age: a mix's are those picked)
const InSetup = (setup, name, age) => setup.cards === CardSets.All
    || (setup.cards === CardSets.Mix ? [...(setup.selection?.[age]?.leaders ?? []), ...(setup.selection?.[age]?.wonders ?? [])].includes(name)
        : (setup.cards === CardSets.Base) === BaseGameCards.has(name));

// A mix's leaders and wonders, picked at random for each age (see Picks): 6 leaders and 4 wonders for two players, 7 and
// 5 for three or four; with the expansion's own, all 6 and 4 of them and one of each from the base game.
// { [age]: { leaders: [names], wonders: [names] } }
const MixCounts = (playerCount) => playerCount <= 2 ? { leaders: 6, wonders: 4 } : { leaders: 7, wonders: 5 };
const PickLeadersAndWonders = (playerCount, random = Math.random, { pick = Picks.All, excluded = [] } = {}) => {
    // count of the names, at random
    const draw = (names, count) => {
        if (names.length < count)
        {
            throw new Error(`Not enough leaders or wonders to pick from: ${count} needed, ${names.length} left in`);
        }
        const pool = [...names];
        for (let i = pool.length - 1; i > 0; i--)
        {
            const j = Math.floor(random() * (i + 1));
            [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        return pool.slice(0, count);
    };
    const Leaders = require('./Leaders/Leaders');
    const Wonders = require('./Wonders/Wonders');
    const { Age } = require('./Constants');
    const counts = MixCounts(playerCount);
    const left = new Set(pick === Picks.Custom ? excluded : []);
    const ofAge = (table, age) => Object.keys(table).filter(name => table[name].age === age && !left.has(name));
    const choose = (table, age, count) => pick === Picks.New
        ? [...ofAge(table, age).filter(name => !BaseGameCards.has(name)), ...draw(ofAge(table, age).filter(name => BaseGameCards.has(name)), 1)]
        : draw(ofAge(table, age), count);
    return Object.fromEntries([Age.AgeA, Age.Age1, Age.Age2, Age.Age3].map(age => [age, {
        leaders: choose(Leaders, age, counts.leaders),
        wonders: choose(Wonders, age, counts.wonders),
    }]));
};

// The leaders and wonders the expansion changed, whose version a custom set can choose
const ChangedLeadersAndWonders = () => {
    const Leaders = require('./Leaders/Leaders');
    const Wonders = require('./Wonders/Wonders');
    return [...Object.keys(Leaders).filter(name => Leaders[name].expansion), ...Object.keys(Wonders).filter(name => Wonders[name].expansion)];
};

// A setup from the choices a new game is started with: { expansion (false: the base game), reveal (Reveals), pick
// (Picks), excluded (names; a custom pick's), overrides (a custom pick's: the version of a changed leader or wonder by
// name, when not the expansion's) }. Throws when they do not make a game (a custom pick leaving too few of an age's
// leaders or wonders for this many players).
const SetupFromChoices = ({ expansion = true, reveal = Reveals.Public, pick = Picks.All, excluded = [], overrides = {} } = {}, playerCount = 4) => {
    if (!expansion)
    {
        return Setups.Base;
    }
    if (!Object.values(Reveals).includes(reveal) || !Object.values(Picks).includes(pick))
    {
        throw new Error(`Unknown setup: reveal one of ${Object.values(Reveals).join(', ')}; pick one of ${Object.values(Picks).join(', ')}`);
    }
    const Leaders = require('./Leaders/Leaders');
    const Wonders = require('./Wonders/Wonders');
    const left = pick === Picks.Custom ? [...new Set(excluded)].filter(name => Leaders[name] || Wonders[name]) : [];
    // (only changed cards still in, in a version there is; the expansion's needs no saying)
    const changed = ChangedLeadersAndWonders();
    const versions = pick === Picks.Custom ? Object.fromEntries(Object.entries(overrides ?? {})
        .filter(([name, version]) => changed.includes(name) && !left.includes(name) && version === Versions.Base)) : {};
    const setup = { ...Expansion, cards: CardSets.Mix, reveal, pick, ...(left.length ? { excluded: left } : {}), overrides: versions };
    // (picking now says whether there are enough; the game picks its own when it is set up)
    PickLeadersAndWonders(playerCount, Math.random, setup);
    return setup;
};

// The choices that start a game like one with this setup (a rematch's), as SetupFromChoices takes them: a setup the
// new game dialog cannot make (every leader and wonder, the expansion's only) as its nearest, the expansion's default
const ChoicesFromSetup = (setup) => {
    if (setup?.cards === CardSets.Base)
    {
        return { expansion: false };
    }
    if (setup?.cards !== CardSets.Mix)
    {
        return { expansion: true };
    }
    return { expansion: true, reveal: setup.reveal ?? Reveals.Public, pick: setup.pick ?? Picks.All,
        ...(setup.pick === Picks.Custom ? { excluded: [...(setup.excluded ?? [])], overrides: { ...(setup.overrides ?? {}) } } : {}) };
};

// The version a game with this setup uses of a card in the table (Leaders or Wonders): only a changed card has one
const SetupVersion = (setup, table, name) => table[name]?.expansion ? (setup.overrides?.[name] ?? setup.versions) : undefined;

// The technologies the expansion changed (their classes take the version: Constants/Technologies), and the version a
// game with this setup uses of one (none: a game from before there was a choice, the base game's)
const TechnologyNames = require('./Names/TechnologyNames');
const ChangedTechnologies = new Set([TechnologyNames.Government.Republic, TechnologyNames.Government.Communism,
    TechnologyNames.Government.Fundamentalism, TechnologyNames.Urban.ProfessionalSports]);
const TechnologyVersion = (setup, name) => setup && ChangedTechnologies.has(name) ? (setup.overrides?.[name] ?? setup.versions) : undefined;

// The territories the expansion changed, by age (Wealthy Territory I: 6 resources, not 5), and the version a game with
// this setup uses of one
const TerritoryNames = require('./Names/TerritoryNames');
const ChangedTerritories = { [TerritoryNames.WealthyTerritory]: ['Age I'] };
const TerritoryVersion = (setup, name, age) => setup && ChangedTerritories[name]?.includes(age) ? (setup.overrides?.[name] ?? setup.versions) : undefined;

// The version a game with this setup uses of a card ({ type, name, age }), or none (one the expansion did not change)
const CardVersion = (setup, card) => {
    switch (card.type)
    {
        case 'Leader': return SetupVersion(setup, require('./Leaders/Leaders'), card.name);
        case 'Wonder': return SetupVersion(setup, require('./Wonders/Wonders'), card.name);
        case 'Technology': return TechnologyVersion(setup, card.name);
        case 'Territory': return TerritoryVersion(setup, card.name, card.age);
        default: return undefined;
    }
};

// A card's version: from the card, or the card data it was made from (the base game's when it says none)
const VersionOf = (card) => card?.version ?? card?.cardData?.version ?? Versions.Base;

// A definition in a version: the expansion's changes over the base game's (the same object each time)
const merged = new Map();
const Definition = (table, name, version) => {
    const base = table[name];
    if (!base?.expansion || version !== Versions.Expansion)
    {
        return base;
    }
    if (!merged.has(base))
    {
        const { expansion, ...rest } = base;
        merged.set(base, { ...rest, ...expansion });
    }
    return merged.get(base);
};

// A leader's or wonder's definition, in the card's version
const LeaderDefinition = (card) => card ? Definition(require('./Leaders/Leaders'), card.name, VersionOf(card)) : undefined;
const WonderDefinition = (card) => card ? Definition(require('./Wonders/Wonders'), card.name, VersionOf(card)) : undefined;
const CardDefinition = (card) => card?.type === 'Leader' ? LeaderDefinition(card) : card?.type === 'Wonder' ? WonderDefinition(card) : undefined;

module.exports = { Versions, CardSets, Reveals, Picks, SetupFromChoices, ChoicesFromSetup, ChangedLeadersAndWonders, DeckSets, ChangedTechnologies, TechnologyVersion, ChangedTerritories, TerritoryVersion,
    CardVersion, MixCounts, PickLeadersAndWonders, SetupNames, Setups, DefaultSetup, BaseGameCards, InSetup, SetupVersion, VersionOf, Definition,
    LeaderDefinition, WonderDefinition, CardDefinition };
